// Quota Management Hook (Quota Frontend Guide Phase 2.2)
// Provides fetch, reset, and adjust operations for survey quotas

import { useState, useEffect, useCallback } from "react";
import { QuotaStatus } from "@/types/survey";
import { API_URL } from "@/lib/api";

interface UseQuotasResult {
  quotas: QuotaStatus[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  // Note: groupId is optional (undefined = global quota)
  // When calling from QuotaStatus, convert null to undefined: groupId ?? undefined
  resetQuota: (segment: string, reason: string, groupId?: string) => Promise<boolean>;
  adjustQuota: (segment: string, adjustment: number, reason: string, groupId?: string) => Promise<boolean>;
}

export function useQuotas(surveyId: number | null): UseQuotasResult {
  const [quotas, setQuotas] = useState<QuotaStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotas = useCallback(async () => {
    if (!surveyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/surveys/${surveyId}/quotas`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch quotas");
      }
      
      const data = await response.json();
      setQuotas(data.quotas || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    fetchQuotas();
  }, [fetchQuotas]);

  const resetQuota = useCallback(async (
    segment: string, 
    reason: string,
    groupId?: string
  ): Promise<boolean> => {
    if (!surveyId) return false;
    
    try {
      const response = await fetch(
        `${API_URL}/api/surveys/${surveyId}/quotas/${encodeURIComponent(segment)}/reset`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason, groupId }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || "Failed to reset quota");
      }
      
      await fetchQuotas(); // Refresh
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      return false;
    }
  }, [surveyId, fetchQuotas]);

  const adjustQuota = useCallback(async (
    segment: string,
    adjustment: number,
    reason: string,
    groupId?: string
  ): Promise<boolean> => {
    if (!surveyId) return false;
    
    try {
      const response = await fetch(
        `${API_URL}/api/surveys/${surveyId}/quotas/${encodeURIComponent(segment)}/adjust`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ adjustment, reason, groupId }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || "Failed to adjust quota");
      }
      
      await fetchQuotas(); // Refresh
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      return false;
    }
  }, [surveyId, fetchQuotas]);

  return {
    quotas,
    loading,
    error,
    refetch: fetchQuotas,
    resetQuota,
    adjustQuota,
  };
}
